// Desktop main shell — the primary logged-in experience.
//
// Layout ("Rooms sidebar + common strip"):
//   ┌ Header: avatar+name · mobile · location switcher · logout ┐
//   ├ COMMON DEVICES strip (always visible for the location)    ┤
//   ├ Rooms sidebar │ selected room's devices │ device panel    ┤
//
// Flow: pick a location → its Common Devices + Rooms load. Pick a room → its
// devices show in the main area. Pick a device (from a room OR the common strip)
// → its controls open in the inline right-side panel, while the common strip and
// rooms stay visible. Common devices reload whenever the location changes.
//
// Pure front-end over existing endpoints — no backend changes.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText, ListItemIcon,
  CircularProgress, IconButton, Divider, Grid, Chip, Button,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  getRooms, getCommonDevices, getDevicesByRoom, getDevicesByUser,
  updateRoomSortOrder, updateDeviceSortOrder,
} from '../api/api';
import { arrayMove } from '../utils/reorder';
import { saveDeviceOrder, applyDeviceOrder } from '../utils/orderCache';
import { useApp } from '../state/AppContext';
import DeviceControlCard from '../components/DeviceControlCard';
import RoomScenesManager from './scenes/RoomScenesManager';
import { deviceGridSpan, deviceTypeKey } from '../devices/deviceMeta';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);
const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

// Rooms and devices carry a SortOrder from the backend; respect it everywhere.
const orderOf = (x) => Number(x.SortOrder ?? x.Sort_Order ?? x.sortorder ?? 0) || 0;
const sortByOrder = (arr) => [...arr].sort((a, b) => orderOf(a) - orderOf(b));

export default function DesktopShell() {
  // Locations, selected location and the Common Devices toggle come from the
  // global header/context.
  const {
    userId, locations, locationId, locationsLoading, locationsError, retryLocations,
    showCommon, editOrder, canReorder,
  } = useApp();
  const reordering = editOrder && canReorder;
  const [dragRoomIdx, setDragRoomIdx] = useState(null);
  const [dragDevIdx, setDragDevIdx] = useState(null);

  const [commonDevices, setCommonDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingLocData, setLoadingLocData] = useState(false);

  const [roomId, setRoomId] = useState(null);
  const [roomDevices, setRoomDevices] = useState([]);
  const [loadingRoom, setLoadingRoom] = useState(false);

  // When the location changes: reload Common Devices + Rooms, clear selections.
  useEffect(() => {
    if (locationId == null) return undefined;
    let alive = true;
    setLoadingLocData(true);
    setRoomId(null);
    setRoomDevices([]);
    (async () => {
      try {
        const [common, rms] = await Promise.all([
          getCommonDevices(userId, locationId).catch(() => null),
          getRooms(userId, locationId).catch(() => null),
        ]);
        if (!alive) return;
        setCommonDevices(sortByOrder(asArray(common)));
        const rooms2 = sortByOrder(asArray(rms));
        setRooms(rooms2);
        // Auto-select the first room (by sort order) so the grid isn't empty.
        setRoomId(rooms2.length ? rooms2[0].RoomID : null);
      } finally {
        if (alive) setLoadingLocData(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, locationId]);

  // Load a room's devices. The room endpoint drops some DeviceIDs server-side
  // (e.g. 23 WWRGB, 3 door, 51 curtain — a legacy exclusion). To match the
  // mobile app, we also pull the user's full device list (no exclusion) and
  // merge in any device that belongs to this room but the room query omitted,
  // matched by Room + Location name.
  const fetchRoomDevices = useCallback(async (rid) => {
    const [byRoom, allRes] = await Promise.all([
      getDevicesByRoom(userId, rid).catch(() => null),
      getDevicesByUser(userId).catch(() => null),
    ]);
    const allDevs = asArray(allRes);
    // The room query omits DevicesInstalled.ID; the all-devices list has it (as
    // `ID`). Enrich every room row so device reorder has its DeviceInstalledID.
    const idByUno = new Map(allDevs.map((d) => [String(unoOf(d)), d.ID]));
    const roomDevs = asArray(byRoom).map((d) => ({ ...d, ID: d.ID ?? idByUno.get(String(unoOf(d))) }));
    const seen = new Set(roomDevs.map((d) => String(unoOf(d))));
    const room = rooms.find((r) => String(r.RoomID) === String(rid));
    const rname = String(room?.RoomName || '').trim().toLowerCase();
    const loc = locations.find((l) => String(l.LocationID) === String(locationId));
    // getLocations returns "<user>-<LocationName>" (e.g. "Sanjay Ailani-Factory")
    // but the all-devices list has the raw name ("Factory"). Match on the suffix.
    const lname = String(loc?.LocationName || '').trim().toLowerCase();
    const locMatches = (dl) => {
      const d = String(dl || '').trim().toLowerCase();
      return !lname || lname === d || lname.endsWith(`-${d}`);
    };
    const extras = rname
      ? allDevs.filter((d) => !seen.has(String(unoOf(d)))
          && String(d.RoomName || '').trim().toLowerCase() === rname
          && locMatches(d.LocationName))
      : [];
    // Backend SortOrder first, then apply the local reorder override (covers the
    // merged-in devices whose SortOrder the API can't return).
    return applyDeviceOrder(userId, rid, sortByOrder([...roomDevs, ...extras]), unoOf);
  }, [userId, rooms, locations, locationId]);

  // When a room is selected: load its devices.
  useEffect(() => {
    if (roomId == null) { setRoomDevices([]); return undefined; }
    let alive = true;
    setLoadingRoom(true);
    fetchRoomDevices(roomId)
      .then((devs) => { if (alive) setRoomDevices(devs); })
      .finally(() => { if (alive) setLoadingRoom(false); });
    return () => { alive = false; };
  }, [roomId, fetchRoomDevices]);

  // Devices refresh their online timestamp (LastUpdatedDateTime) in the DB every
  // ~30s. Re-fetch the current room + common devices on the same cadence — one
  // list call each — so the online/offline dots stay live without a page reload.
  useEffect(() => {
    const id = setInterval(async () => {
      if (editOrder) return; // don't reshuffle mid-drag
      try {
        if (roomId != null) {
          const devs = await fetchRoomDevices(roomId);
          if (devs) setRoomDevices(devs);
        }
        if (locationId != null) {
          const common = await getCommonDevices(userId, locationId).catch(() => null);
          if (common) setCommonDevices(sortByOrder(asArray(common)));
        }
      } catch (e) { /* ignore transient poll errors */ }
    }, 30000);
    return () => clearInterval(id);
  }, [userId, roomId, locationId, fetchRoomDevices, editOrder]);

  // Persist a new room order (global — Rooms.SortOrder) then reflect it.
  const persistRoomOrder = async (list) => {
    setRooms(list.map((r, i) => ({ ...r, SortOrder: i })));
    await Promise.all(list.map((r, i) => updateRoomSortOrder(r.RoomID, i).catch(() => null)));
  };
  const onDropRoom = (i) => {
    if (dragRoomIdx != null && dragRoomIdx !== i) persistRoomOrder(arrayMove(rooms, dragRoomIdx, i));
    setDragRoomIdx(null);
  };
  const moveRoom = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < rooms.length) persistRoomOrder(arrayMove(rooms, i, j));
  };

  // Persist a new device order: cache locally (reliable for every device type,
  // incl. merged-in ones) AND write backend SortOrder where we have the ID.
  const persistDeviceOrder = async (list) => {
    setRoomDevices(list.map((d, i) => ({ ...d, SortOrder: i })));
    saveDeviceOrder(userId, roomId, list.map((d) => unoOf(d)));
    await Promise.all(list.map((d, i) => (d.ID != null
      ? updateDeviceSortOrder(userId, d.ID, i).catch(() => null) : null)));
  };
  const onDropDev = (i) => {
    if (dragDevIdx != null && dragDevIdx !== i) persistDeviceOrder(arrayMove(roomDevices, dragDevIdx, i));
    setDragDevIdx(null);
  };
  const moveDev = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < roomDevices.length) persistDeviceOrder(arrayMove(roomDevices, i, j));
  };

  const selectedRoom = rooms.find((r) => String(r.RoomID) === String(roomId));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
      {locationsLoading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : locations.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">
            {locationsError
              ? "Couldn't load your locations. Check your connection and try again."
              : 'No locations available for this account.'}
          </Typography>
          {locationsError && (
            <Button variant="contained" onClick={retryLocations}>Retry</Button>
          )}
        </Box>
      ) : (
        <>
          {/* Common Devices strip — shown only when the header toggle is green. */}
          {showCommon && (
            <>
              <CommonStrip devices={commonDevices} loading={loadingLocData} />
              <Divider />
            </>
          )}

          {/* Rooms | room devices | device controls */}
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Rooms sidebar */}
            <Box sx={{ width: 240, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, display: 'block' }} color="text.secondary">
                Rooms
              </Typography>
              {loadingLocData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={22} /></Box>
              ) : rooms.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  No rooms in this location
                </Typography>
              ) : (
                <List dense>
                  {rooms.map((r, i) => (
                    <ListItemButton
                      key={r.RoomID}
                      selected={String(r.RoomID) === String(roomId)}
                      onClick={() => setRoomId(r.RoomID)}
                      draggable={reordering}
                      onDragStart={reordering ? (e) => { setDragRoomIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                      onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                      onDrop={reordering ? (e) => { e.preventDefault(); onDropRoom(i); } : undefined}
                      sx={reordering ? { cursor: 'grab' } : undefined}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'text.primary' }}>
                        {reordering ? <DragIndicatorIcon fontSize="small" /> : <MeetingRoomIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={r.RoomName || `Room ${r.RoomID}`}
                        primaryTypographyProps={{ color: 'text.primary', noWrap: true }}
                      />
                      {reordering && (
                        <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" disabled={i === 0} onClick={() => moveRoom(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                          <IconButton size="small" disabled={i === rooms.length - 1} onClick={() => moveRoom(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            {/* Selected room's devices */}
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
              {roomId == null ? (
                <EmptyHint icon={<MeetingRoomIcon />} text="Select a room to see its devices" />
              ) : loadingRoom ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Typography variant="h6" color="text.primary">
                      {selectedRoom?.RoomName || 'Room'}
                    </Typography>
                  </Box>
                  {/* Per-room Scenes (max 8) — right-click to add; right-click a
                      scene to edit/delete; left-click to run. */}
                  <RoomScenesManager
                    key={roomId}
                    variant="inline"
                    roomId={roomId}
                    roomName={selectedRoom?.RoomName || 'Room'}
                    initialDevices={roomDevices}
                  />
                  {roomDevices.length === 0 ? (
                    <EmptyHint icon={<DevicesOtherIcon />} text="No devices in this room" />
                  ) : (
                    <Grid container spacing={1.25} alignItems="stretch">
                      {roomDevices.map((d, i) => (
                        <Grid item {...deviceGridSpan(d.DeviceID)} key={unoOf(d)} sx={{ display: 'flex' }}>
                          <Box
                            sx={{ width: '100%', position: 'relative', cursor: reordering ? 'grab' : 'default' }}
                            draggable={reordering}
                            onDragStart={reordering ? (e) => { setDragDevIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } : undefined}
                            onDragOver={reordering ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                            onDrop={reordering ? (e) => { e.preventDefault(); onDropDev(i); } : undefined}
                          >
                            {reordering && (
                              <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === 0} onClick={() => moveDev(i, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                                <DragIndicatorIcon fontSize="small" sx={{ color: '#fff' }} />
                                <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={i === roomDevices.length - 1} onClick={() => moveDev(i, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                              </Box>
                            )}
                            <Box sx={{ pointerEvents: reordering ? 'none' : 'auto' }}>
                              <DeviceControlCard device={d} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

function CommonStrip({ devices, loading }) {
  return (
    <Box sx={{ px: 2, py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <GroupWorkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="overline" color="text.secondary">Common Devices</Typography>
        {!loading && <Chip size="small" label={devices.length} />}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No common devices for this location</Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, alignItems: 'stretch' }}>
          {devices.map((d) => {
            const k = deviceTypeKey(d.DeviceID);
            const w = (k === 'singlePhase' || k === 'threePhase' || k === 'wwrgb') ? 380 : 230;
            return (
              <Box key={unoOf(d)} sx={{ width: w, flexShrink: 0 }}>
                <DeviceControlCard device={d} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function EmptyHint({ icon, text }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 8, color: 'text.secondary' }}>
      <Box sx={{ fontSize: 48, display: 'flex' }}>{icon}</Box>
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  );
}
